import type { AppIconName } from "@/components/common/app-icon";
import type React from "react";

import { type SidebarItem } from "./types";

import {
  TUTORIAL_AREAS,
  TUTORIAL_GROUPS,
  tutorialAreaPath,
} from "@/config/tutorials";
import { BetaChip } from "@/components/ui/beta-chip";
import { AppointmentsAttentionBadge } from "@/features/clinic/components/appointments/attention-nav-badge";
import {
  ECHO_REPORT_PATH,
  ECHO_TABS,
} from "@/features/reports/ai/constants/echo-performance";
import {
  COLLECTIONS_REPORT_PATH,
  COLLECTIONS_TABS,
} from "@/features/reports/collections/constants";
import { HANDOVERS_REPORT_PATH } from "@/features/reports/handovers/constants";
import {
  AI_ASSISTANT,
  CONSTANTS,
  assistantPath,
  isTutorialsEnabled,
} from "@/config/constants";
import { FEATURE_FLAG, FeatureFlag } from "@/types/feature-flag";
import { ORGANIZATION_TYPE, OrganizationType } from "@/types/organization";
import { AI_FEATURE, AiFeature } from "@/types/workspace";

// Contextual filter sub-trees the data model can't express as items/groups.
export type SidebarSlot =
  | "inbox-filters"
  | "contact-filters"
  | "ai-agent-sections";

// Hides a row unless the named backend feature flag, or the workspace's AI
// feature, is on. A group left with no children disappears entirely.
export type SidebarGates = {
  requiresFlag?: FeatureFlag;
  requiresAiFeature?: AiFeature;
};

export type MainSidebarItemNode = SidebarGates & {
  kind: "item";
  key: string;
  href: string;
  icon: AppIconName;
  /** Icon size in px when 18 reads wrong, e.g. Echo's solid mark. */
  iconSize?: number;
  title: string;
  endContent?: React.ReactNode;
  trailing?: "chevron-right";
  internalOnly?: boolean;
  // Extra keywords the sidebar search matches beyond the title (e.g. renamed
  // items keep their old, well-known names findable).
  searchTerms?: string[];
  // Sub-tree this item shows in the section panel (column 2), instead of the
  // layout hardcoding which item keys own which slot.
  sectionSlot?: SidebarSlot;
  // Extra route prefixes that still count as "inside this section", for pages
  // the item's own href doesn't cover (Inbox links to the thread list but owns
  // the whole /inbox tree).
  sectionPaths?: string[];
};

export type MainSidebarGroupChild = SidebarGates & {
  key: string;
  href: string;
  title: string;
  icon?: AppIconName;
  endContent?: React.ReactNode;
  internalOnly?: boolean;
  /** Rows nested under this one while its page is open, e.g. Billing's views. */
  views?: React.ReactNode;
};

export type MainSidebarGroupSection = {
  key: string;
  label: string;
  children: MainSidebarGroupChild[];
};

export type MainSidebarGroupNode = {
  kind: "group";
  key: string;
  href: string;
  icon: AppIconName;
  title: string;
  // A group renders either a flat child list (`children`) or labelled
  // subsections (`sections`). Use `getGroupChildren` to read the flat list
  // regardless of shape.
  children?: MainSidebarGroupChild[];
  sections?: MainSidebarGroupSection[];
  // Extra keywords the sidebar search matches beyond the title.
  searchTerms?: string[];
};

// Flattens a group's children whether it uses `children` or `sections`, so
// filtering / search / active-state logic can stay shape-agnostic.
export const getGroupChildren = (
  group: MainSidebarGroupNode,
): MainSidebarGroupChild[] =>
  group.sections
    ? group.sections.flatMap((section) => section.children)
    : (group.children ?? []);

// Normalises any group to a sections array for rendering: a flat group becomes
// a single label-less section, so the render path never branches on shape.
export const getGroupSections = (
  group: MainSidebarGroupNode,
): MainSidebarGroupSection[] =>
  group.sections ?? [
    { key: `${group.key}-children`, label: "", children: group.children ?? [] },
  ];

// Returns a copy of the group keeping only children that satisfy `predicate` -
// preserving section structure and dropping emptied sections - or null when
// nothing remains. Shared by access-filtering and search so both group shapes
// are handled in exactly one place.
export const filterGroupChildren = (
  group: MainSidebarGroupNode,
  predicate: (child: MainSidebarGroupChild) => boolean,
): MainSidebarGroupNode | null => {
  if (group.sections) {
    const sections = group.sections
      .map((section) => ({
        ...section,
        children: section.children.filter(predicate),
      }))
      .filter((section) => section.children.length > 0);

    return sections.length > 0 ? { ...group, sections } : null;
  }
  const children = (group.children ?? []).filter(predicate);

  return children.length > 0 ? { ...group, children } : null;
};

export type MainSidebarDividerNode = {
  kind: "divider";
  key: string;
  // A labelled divider renders as a muted zone heading ("Manage") instead of
  // being invisible - the seam between daily-work and admin sections.
  label?: string;
};

export type MainSidebarNode =
  | MainSidebarItemNode
  | MainSidebarGroupNode
  | MainSidebarDividerNode;

// Home is /dashboard for everyone - the page itself splits by role (reporting
// dashboard for admins/members, the personal "My Activities" queue for
// agents). The inbox href is rewritten with the active workspace id in
// useSidebarItems.
const MAIN_GROUP: MainSidebarNode[] = [
  {
    kind: "item",
    key: "my-inbox",
    href: "/dashboard",
    icon: "solar:home-2-linear",
    title: "Home",
    searchTerms: ["my inbox", "dashboard", "activities", "reports"],
  },
  {
    kind: "item",
    key: "inbox",
    // The thread list is the `inbox/:workspaceId` INDEX route. Any extra
    // segment here would match the sibling `:contactId` param instead and send
    // the page off to fetch a contact by that literal string. Home's exact
    // /activities href is longer, so it still wins the active state there.
    href: "/inbox/1",
    icon: "solar:inbox-linear",
    title: "Inbox",
    searchTerms: ["conversations", "threads", "messages"],
    sectionSlot: "inbox-filters",
    // Claims the rest of the inbox tree (an open conversation is /inbox/:ws/
    // :contactId), so the filters stay put while reading a thread.
    sectionPaths: ["/inbox"],
  },
];

const CRM_GROUP: MainSidebarNode[] = [
  {
    kind: "item",
    key: "contacts",
    href: "/contacts",
    icon: "solar:users-group-rounded-linear",
    title: "Contacts",
    sectionSlot: "contact-filters",
  },
];

const ENGAGE_GROUP: MainSidebarNode[] = [
  {
    kind: "item",
    key: "broadcasts",
    href: "/broadcasts",
    icon: "solar:letter-linear",
    title: "Broadcasts",
    searchTerms: ["engage", "outreach", "campaigns"],
  },
  {
    kind: "item",
    key: "ads",
    href: "/ads",
    icon: "solar:chart-square-linear",
    title: "Ads",
    searchTerms: ["engage", "advertising"],
  },
  {
    kind: "item",
    key: "workflows",
    href: "/workflows",
    icon: "solar:routing-2-linear",
    title: "Workflows",
    endContent: <BetaChip />,
    searchTerms: ["engage", "automation"],
  },
  {
    kind: "item",
    key: AI_ASSISTANT.slug,
    href: assistantPath(),
    icon: AI_ASSISTANT.icon,
    iconSize: 22,
    title: AI_ASSISTANT.label,
    endContent: <BetaChip />,
    sectionSlot: "ai-agent-sections",
    requiresAiFeature: AI_FEATURE.ECHO,
  },
];

const ECOMMERCE_CHILDREN: MainSidebarGroupChild[] = [
  {
    key: "ecommerce-products",
    href: "/ecommerce/products",
    title: "Products",
    icon: "solar:box-linear",
  },
  {
    key: "ecommerce-categories",
    href: "/ecommerce/categories",
    title: "Categories",
    icon: "solar:folder-linear",
  },
  {
    key: "ecommerce-brands",
    href: "/ecommerce/brands",
    title: "Brands",
    icon: "solar:verified-check-linear",
  },
  {
    key: "ecommerce-orders",
    href: "/ecommerce/orders",
    title: "Orders",
    icon: "solar:cart-large-2-linear",
  },
  {
    key: "ecommerce-discounts",
    href: "/ecommerce/discounts",
    title: "Discounts",
    icon: "solar:ticket-sale-linear",
  },
  {
    key: "ecommerce-variant-attributes",
    href: "/ecommerce/variant-attributes",
    title: "Variant Attributes",
    icon: "solar:tag-horizontal-linear",
  },
  {
    key: "ecommerce-store",
    href: "/ecommerce/store",
    title: "Storefronts",
    icon: "solar:shop-linear",
  },
  {
    key: "tickets",
    href: "/tickets",
    title: "Tickets",
    icon: "solar:ticket-linear",
  },
];

const COMMERCE_GROUPED: MainSidebarNode[] = [
  {
    kind: "group",
    key: "ecommerce",
    href: "/ecommerce",
    icon: "solar:shop-2-linear",
    title: "Ecommerce",
    children: ECOMMERCE_CHILDREN,
  },
];

const CLINIC_CHILDREN: MainSidebarGroupChild[] = [
  {
    key: "appointments",
    href: "/appointments",
    title: "Appointments",
    icon: "solar:calendar-linear",
    endContent: <AppointmentsAttentionBadge />,
  },
  {
    key: "treatments",
    href: "/treatments",
    title: "Treatments",
    icon: "hugeicons:treatment",
  },
  {
    key: "doctors",
    href: "/doctors",
    title: "Doctors",
    icon: "mdi:doctor",
  },
  {
    key: "departments",
    href: "/departments",
    title: "Departments",
    icon: "solar:buildings-3-linear",
  },
  {
    key: "clinic-billing",
    href: "/clinics/billing",
    title: "Billing",
    icon: "solar:bill-list-linear",
  },
];

const CLINIC_GROUPED: MainSidebarNode[] = [
  {
    kind: "group",
    key: "clinic",
    href: "/appointments",
    icon: "solar:health-linear",
    title: "Clinic",
    children: CLINIC_CHILDREN,
  },
  {
    kind: "group",
    key: "supplies",
    href: "/supplies/inventory",
    icon: "solar:box-linear",
    title: "Supplies",
    children: [
      {
        key: "supplies-inventory",
        href: "/supplies/inventory",
        title: "Inventory",
        icon: "solar:box-minimalistic-linear",
        requiresFlag: FEATURE_FLAG.SUPPLIES,
      },
      {
        key: "supplies-catalogue",
        href: "/supplies/catalogue",
        title: "Catalogue",
        icon: "solar:shop-2-linear",
        requiresFlag: FEATURE_FLAG.SUPPLIES,
      },
      {
        key: "supplies-orders",
        href: "/supplies/orders",
        title: "Orders",
        icon: "solar:clipboard-list-linear",
        requiresFlag: FEATURE_FLAG.SUPPLIES,
      },
      {
        key: "supplies-order-requests",
        href: "/supplies/order-requests",
        title: "Order requests",
        icon: "solar:inbox-in-linear",
        requiresFlag: FEATURE_FLAG.SUPPLIES,
      },
    ],
  },
];

/** Clinic-only, and mirrors the route guard on /reports/appointments. */
const APPOINTMENTS_REPORT_CHILD = {
  key: "reports-appointments",
  href: "/reports/appointments",
  title: "Overview",
  icon: "solar:calendar-linear",
};

/** Clinic-only, and mirrors the route guard on /reports/attendance. */
const ATTENDANCE_REPORT_CHILD = {
  key: "reports-attendance",
  href: "/reports/attendance",
  title: "Recoveries",
  icon: "solar:calendar-mark-linear",
};

/** Clinic-only, and mirrors the route guard on /reports/doctors. */
const DOCTORS_REPORT_CHILD = {
  key: "reports-doctors",
  href: "/reports/doctors",
  title: "Doctors & treatments",
  icon: "solar:stethoscope-linear",
};

/** Clinic-only, and mirrors the route guard on /reports/collections. */
const COLLECTIONS_REPORT_CHILDREN = COLLECTIONS_TABS.map(
  ({ id, label, icon }) => ({
    key: `reports-collections-${id}`,
    href: `${COLLECTIONS_REPORT_PATH}/${id}`,
    title: label,
    icon,
  }),
);

/** Every organization type: a clinic sees visits, a store sees orders. */
const DAILY_OPERATIONS_REPORT_CHILD = {
  key: "reports-daily-operations",
  href: "/reports/daily-operations",
  title: "Daily operations",
  icon: "solar:checklist-minimalistic-linear",
};

/** Ecommerce-only, and mirrors the route guard on /reports/sales. */
const SALES_REPORT_SECTION = {
  key: "reports-sales-pages",
  label: "Sales",
  children: [
    {
      key: "reports-sales",
      href: "/reports/sales",
      title: "Overview",
      icon: "solar:chart-2-linear",
    },
    {
      key: "reports-sales-products",
      href: "/reports/sales/products",
      title: "Products",
      icon: "solar:box-linear",
    },
    {
      key: "reports-sales-customers",
      href: "/reports/sales/customers",
      title: "Customers",
      icon: "solar:users-group-two-rounded-linear",
    },
    {
      key: "reports-sales-deliveries",
      href: "/reports/sales/deliveries",
      title: "Deliveries & payments",
      icon: "solar:delivery-linear",
    },
  ],
};

/** Clinic-only, and mirrors the route guard on /reports/ai. */
const AI_REPORT_CHILDREN = ECHO_TABS.map(({ id, label, icon }) => ({
  key: `reports-ai-${id}`,
  href: `${ECHO_REPORT_PATH}/${id}`,
  title: label,
  icon,
  requiresAiFeature: AI_FEATURE.ECHO,
}));

/** Any organization type, and mirrors the route guard on /reports/handovers. */
const HANDOVERS_REPORT_CHILD = {
  key: "reports-handovers",
  href: HANDOVERS_REPORT_PATH,
  title: "Handovers",
  icon: "solar:user-hand-up-linear",
  requiresAiFeature: AI_FEATURE.ECHO,
};

// Its subsections are the same groups the /reports landing page renders as
// cards - one list, two surfaces.
export const REPORTS_SECTION_KEY = "reports";

const reportsGroup = (
  organizationType?: OrganizationType,
): MainSidebarNode[] => [
  {
    kind: "group",
    key: REPORTS_SECTION_KEY,
    href: "/reports",
    icon: "solar:diagram-down-outline",
    title: "Reports",
    sections: [
      {
        key: "reports-conversations-pages",
        label: "Conversations",
        children: [
          {
            key: "reports-conversations",
            href: "/reports/conversations",
            title: "Overview",
            icon: "solar:chat-round-line-linear",
          },
          {
            key: "reports-messages",
            href: "/reports/messages",
            title: "Messages",
            icon: "solar:letter-linear",
          },
        ],
      },
      {
        key: "reports-daily",
        label: "Daily",
        children: [
          {
            key: "reports-daily-tracker",
            href: "/reports/daily-tracker",
            title: "Daily tracker",
            icon: "solar:clipboard-list-linear",
          },
          DAILY_OPERATIONS_REPORT_CHILD,
        ],
      },
      ...(organizationType === ORGANIZATION_TYPE.ECOMMERCE
        ? [SALES_REPORT_SECTION]
        : []),
      {
        key: "reports-contacts",
        label: "Contacts",
        children: [
          {
            key: "reports-contacts-overview",
            href: "/reports/contacts",
            title: "Overview",
            icon: "solar:users-group-rounded-linear",
          },
          {
            key: "reports-leads",
            href: "/reports/leads",
            title: "Leads",
            icon: "solar:user-plus-linear",
          },
          {
            key: "reports-lost",
            href: "/reports/lost",
            title: "Lost & objections",
            icon: "solar:user-cross-linear",
          },
          {
            key: "reports-sources",
            href: "/reports/sources",
            title: "Sources & ads",
            icon: "solar:target-linear",
          },
          {
            key: "reports-broadcasts",
            href: "/reports/broadcasts",
            title: "Broadcasts",
            icon: "solar:letter-linear",
          },
          {
            key: "reports-follow-ups",
            href: "/reports/follow-ups",
            title: "Follow-ups",
            icon: "solar:bell-linear",
          },
        ],
      },
      ...(organizationType === ORGANIZATION_TYPE.CLINIC
        ? [
            {
              key: "reports-appointments",
              label: "Appointments",
              children: [
                APPOINTMENTS_REPORT_CHILD,
                ATTENDANCE_REPORT_CHILD,
                DOCTORS_REPORT_CHILD,
              ],
            },
            {
              key: "reports-collections",
              label: "Collections",
              children: COLLECTIONS_REPORT_CHILDREN,
            },
          ]
        : []),
      {
        key: "reports-echo",
        label: AI_ASSISTANT.name,
        children: [
          ...(organizationType === ORGANIZATION_TYPE.CLINIC
            ? AI_REPORT_CHILDREN
            : []),
          HANDOVERS_REPORT_CHILD,
        ],
      },
      {
        key: "reports-performance",
        label: "Performance",
        children: [
          {
            key: "reports-agents",
            href: "/reports/agents",
            title: "Team performance",
            icon: "solar:medal-ribbon-star-linear",
          },
        ],
      },
    ],
  },
];

// The single admin destination. Its subsections are the same four zones the
// /settings landing page renders as cards - one list, two surfaces.
export const SETTINGS_SECTION_KEY = "settings";

const TUTORIALS_GROUP: MainSidebarGroupNode = {
  kind: "group",
  key: "tutorials",
  href: "/tutorials",
  icon: "solar:book-2-linear",
  title: "Tutorials",
  children: TUTORIAL_GROUPS.map((group) => ({
    key: `tutorials-${group}`,
    href: tutorialAreaPath(group),
    title: TUTORIAL_AREAS[group].navTitle ?? group,
    icon: TUTORIAL_AREAS[group].icon,
  })),
};

const settingsGroup = (
  organizationType?: OrganizationType,
): MainSidebarGroupNode => ({
  kind: "group",
  key: SETTINGS_SECTION_KEY,
  href: "/settings",
  icon: "solar:settings-linear",
  title: "Settings",
  searchTerms: [
    "organization",
    "workspace",
    "connections",
    "inbox",
    "content",
    "admin",
    "ai",
    "credits",
    "billing",
  ],
  sections: [
    {
      key: "settings-section-organization",
      label: "Organization",
      children: [
        {
          key: "settings-organization",
          href: "/settings/organization",
          title: "General",
          icon: "solar:buildings-3-linear",
        },
        {
          key: "settings-users",
          href: "/settings/users",
          title: "Members",
          icon: "solar:users-group-rounded-linear",
        },
        ...(CONSTANTS.SELF_SERVE_SUBSCRIPTION
          ? [
              {
                key: "settings-subscription",
                href: "/settings/subscription",
                title: "Plan & billing",
                icon: "solar:card-recive-linear",
                requiresFlag: FEATURE_FLAG.STRIPE_PAYMENTS,
              },
            ]
          : []),
      ],
    },
    {
      key: "settings-section-workspace",
      label: "Workspace",
      children: [
        {
          key: "settings-workspace",
          href: "/settings/workspace",
          title: "General",
          icon: "solar:settings-linear",
        },
        {
          key: "settings-workspace-users",
          href: "/settings/workspace-users",
          title: "Members",
          icon: "solar:users-group-rounded-linear",
        },
        {
          key: "settings-teams",
          href: "/settings/teams",
          title: "Teams",
          icon: "solar:users-group-two-rounded-linear",
        },
        {
          key: "settings-roles",
          href: "/settings/roles",
          title: "Roles",
          icon: "solar:shield-user-linear",
        },
        {
          key: "settings-contact-fields",
          href: "/settings/contact-fields",
          title: "Contact Fields",
          icon: "solar:widget-add-linear",
        },
        {
          key: "settings-websites",
          href: "/settings/websites",
          title: "Websites",
          icon: "solar:global-linear",
        },
        {
          key: "settings-developer",
          href: "/settings/developer",
          title: "API Keys",
          icon: "solar:code-linear",
        },
      ],
    },
    // AI spend is configured in one place rather than scattered across
    // Workspace and Inbox.
    {
      key: "settings-section-ai",
      label: "AI",
      children: [
        {
          key: "settings-ai",
          href: "/settings/ai",
          title: "General",
          icon: AI_ASSISTANT.icon,
        },
        {
          key: "settings-credits",
          href: "/settings/credits",
          title: "Credits",
          icon: "solar:wallet-money-linear",
        },
      ],
    },
    {
      key: "settings-section-connections",
      label: "Connections",
      children: [
        {
          key: "settings-channels",
          href: "/channels",
          title: "Channels",
          icon: "solar:link-linear",
        },
        {
          key: "settings-integrations",
          href: "/integrations",
          title: "Integrations",
          icon: "solar:plug-circle-linear",
        },
        ...(organizationType === ORGANIZATION_TYPE.CLINIC
          ? [
              {
                key: "settings-supplies",
                href: "/settings/supplies",
                title: "Supplies",
                icon: "solar:box-linear",
                requiresFlag: FEATURE_FLAG.SUPPLIES,
              },
            ]
          : []),
      ],
    },
    {
      key: "settings-section-inbox",
      label: "Inbox",
      children: [
        {
          key: "settings-inbox",
          href: "/settings/inbox",
          title: "Hours",
          icon: "solar:clock-circle-linear",
        },
        {
          key: "settings-sla",
          href: "/settings/sla",
          title: "SLAs",
          icon: "solar:stopwatch-linear",
        },
        {
          key: "settings-assignment",
          href: "/settings/assignment",
          title: "Assignment",
          icon: "solar:users-group-two-rounded-linear",
        },
        {
          key: "settings-lifecycle",
          href: "/settings/lifecycle",
          title: "Lifecycle",
          icon: "solar:traffic-linear",
        },
        {
          key: "settings-tags",
          href: "/settings/tags",
          title: "Tags",
          icon: "solar:tag-linear",
        },
        {
          key: "settings-wrap-up",
          href: "/settings/wrap-up",
          title: "Wrap-Up",
          icon: "solar:clipboard-check-linear",
        },
        {
          key: "settings-closing-notes",
          href: "/settings/closing-notes",
          title: "Closing Notes",
          icon: "solar:document-text-linear",
        },
        // Reasons explain appointment outcomes, so only clinics have a list.
        ...(organizationType === ORGANIZATION_TYPE.CLINIC
          ? [
              {
                key: "settings-outcome-reasons",
                href: "/settings/outcome-reasons",
                title: "Outcome Reasons",
                icon: "solar:question-circle-linear",
              },
            ]
          : []),
      ],
    },
    {
      key: "settings-section-content",
      label: "Content",
      children: [
        {
          key: "settings-snippets",
          href: "/settings/snippets",
          title: "Snippets",
          icon: "solar:notes-linear",
        },
        {
          key: "settings-files",
          href: "/settings/files",
          title: "Files",
          icon: "solar:folder-with-files-linear",
        },
        {
          key: "settings-templates",
          href: "/settings/templates",
          title: "WhatsApp templates",
          icon: "solar:folder-linear",
        },
      ],
    },
  ],
});

const divider = (key: string, label?: string): MainSidebarDividerNode => ({
  kind: "divider",
  key: `divider-${key}`,
  label,
});

export const getMainSidebarNodes = (
  organizationType?: OrganizationType,
  useFlatNav?: boolean,
): MainSidebarNode[] => {
  // Each vertical waits for its own type, so neither shows before the
  // organization loads.
  const verticalGroup =
    organizationType === ORGANIZATION_TYPE.CLINIC
      ? CLINIC_GROUPED
      : organizationType === ORGANIZATION_TYPE.ECOMMERCE
        ? COMMERCE_GROUPED
        : [];

  const nodes: MainSidebarNode[] = [
    ...MAIN_GROUP,
    divider("crm"),
    ...CRM_GROUP,
    divider("engage"),
    ...ENGAGE_GROUP,
    divider("vertical"),
    ...verticalGroup,
    divider("reports"),
    ...reportsGroup(organizationType),
    divider("settings"),
    settingsGroup(organizationType),
    // Beta only while QA signs the tutorials off.
    ...(isTutorialsEnabled() ? [TUTORIALS_GROUP] : []),
  ];

  // Dissolving the groups only reads as a simplification because access
  // filtering then cuts the hoisted children down to the few an agent can
  // reach. Callers must not ask for it on behalf of someone who passes every
  // route check - see `useSidebarItems`, which is where the roles live.
  if (!useFlatNav) return nodes;

  return nodes.flatMap<MainSidebarNode>((node) => {
    if (node.kind !== "group") return node;
    // One Tutorials entry, not six areas: the area rail belongs to the grouped layout.
    if (node.key === "tutorials") {
      return {
        kind: "item" as const,
        key: node.key,
        href: node.href,
        icon: node.icon,
        title: node.title,
      };
    }

    return getGroupChildren(node).map((child) => ({
      ...child,
      kind: "item" as const,
      icon: child.icon ?? node.icon,
      searchTerms: node.searchTerms,
    }));
  });
};

export const CLINIC_ITEMS: SidebarItem[] = [
  {
    key: "appointments",
    href: "/appointments",
    icon: "solar:calendar-linear",
    title: "Appointments",
  },
  {
    key: "treatments",
    href: "/treatments",
    icon: "hugeicons:treatment",
    title: "Treatments",
  },
  {
    key: "doctors",
    href: "/doctors",
    icon: "mdi:doctor",
    title: "Doctors",
  },
  {
    key: "departments",
    href: "/departments",
    icon: "solar:buildings-3-linear",
    title: "Departments",
  },
  {
    key: "clinic-billing",
    href: "/clinics/billing",
    icon: "solar:bill-list-linear",
    title: "Billing",
  },
];
